import { h, octButton, ICONS } from './kit';

/**
 * The stack of full screens over the scene (onboarding, settings, collection…). One way back for
 * everything: the header's «back», Esc, Telegram's Back button (via `back()`), each closing the top
 * screen. Keys pressed inside a screen never reach the game's shortcuts underneath.
 */
export interface ScreenSpec {
  el: HTMLElement;
  /** Back was asked for: return true when the screen handled it itself (a previous page). */
  onBack?(): boolean | void;
  /** The screen has left (by back or close). */
  onClose?(): void;
  /** Covers the whole scene: nothing behind it needs drawing. */
  covers?: boolean;
  /** Back can't close it (a step that must be finished, like the first nick); `onBack` still runs. */
  locked?: boolean;
  /** Rises from below instead of sliding in from the side (dialogs, the in-match menu). */
  rise?: boolean;
  /** Focus this on open (else the screen itself). */
  focus?: HTMLElement | null;
}

interface Entry { spec: ScreenSpec; returnFocus: Element | null }

const LEAVE_MS = 320;

export class Screens {
  readonly root: HTMLElement;
  private readonly stack: Entry[] = [];
  /** The stack changed: whether any screen is open and whether the scene is covered. */
  onChange: (open: boolean, covered: boolean) => void = () => {};

  constructor(parent: HTMLElement = document.body) {
    this.root = h('div', { class: 'ui-root' });
    parent.append(this.root);
    // keys typed in a screen stay there (the game listens on window: 1, Space, M…)
    this.root.addEventListener('keydown', (e) => { if (e.key !== 'Escape') e.stopPropagation(); });
    // Esc goes back before anything else hears it
    window.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || !this.stack.length || e.repeat) return;
      e.preventDefault(); e.stopImmediatePropagation();
      this.back();
    }, true);
  }

  get size() { return this.stack.length; }
  get top(): ScreenSpec | null { return this.stack.at(-1)?.spec ?? null; }
  has(spec: ScreenSpec) { return this.stack.some((e) => e.spec === spec); }

  open(spec: ScreenSpec): ScreenSpec {
    if (this.has(spec)) return spec;
    const el = spec.el;
    el.classList.add('ui-screen');
    el.classList.toggle('ui-rise', !!spec.rise);
    el.tabIndex = -1;
    el.setAttribute('role', el.getAttribute('role') ?? 'dialog');
    el.setAttribute('aria-modal', 'true');
    const under = this.top;
    this.stack.push({ spec, returnFocus: document.activeElement });
    el.setAttribute('data-enter', '');
    this.root.append(el);
    // two frames: the start state is drawn before the transition to the end state begins
    requestAnimationFrame(() => requestAnimationFrame(() => {
      el.removeAttribute('data-enter');
      if (under && this.has(under)) under.el.setAttribute('data-under', '');
    }));
    (spec.focus ?? el).focus({ preventScroll: true });
    this.changed();
    return spec;
  }

  /** Back from the top screen: its own previous page, else it closes. False when nothing is open. */
  back(): boolean {
    const top = this.top;
    if (!top) return false;
    if (top.onBack?.() || top.locked) return true;
    this.close(top);
    return true;
  }

  /** Closes this screen (and any opened above it). */
  close(spec: ScreenSpec) {
    const i = this.stack.findIndex((e) => e.spec === spec);
    if (i < 0) return;
    const gone = this.stack.splice(i);
    for (const { spec: s } of gone.reverse()) {
      s.el.setAttribute('data-leave', '');
      s.el.removeAttribute('data-under');
      let done = false;
      const remove = () => { if (done) return; done = true; s.el.remove(); s.el.removeAttribute('data-leave'); };
      s.el.addEventListener('transitionend', (e) => { if (e.target === s.el) remove(); });
      setTimeout(remove, LEAVE_MS);
      s.onClose?.();
    }
    const now = this.top;
    now?.el.removeAttribute('data-under');
    const back = gone[gone.length - 1]?.returnFocus as HTMLElement | null;
    if (now) (now.focus ?? now.el).focus({ preventScroll: true });
    else if (back?.isConnected) back.focus?.({ preventScroll: true });
    this.changed();
  }

  /** Closes everything (a match starts, the account changed). */
  clear() { if (this.stack.length) this.close(this.stack[0].spec); }

  private changed() {
    if (this.stack.length) this.root.setAttribute('data-open', String(this.stack.length));
    else this.root.removeAttribute('data-open');
    const covered = this.stack.some((e) => e.spec.covers);
    // what a covering screen hides stops its endless animations (ui.css): shadows and filters cost a phone
    document.documentElement.toggleAttribute('data-covered', covered);
    this.onChange(this.stack.length > 0, covered);
  }
}

/** A screen's frame: «back» and the title on top, a scrolling body under it. */
export function screenFrame(title: string, onBack: (() => void) | null, ...extra: (Node | null)[]) {
  const header = h('div', { class: 'ui-header' },
    onBack ? octButton(ICONS.back, 'Назад', { onClick: onBack }) : null,
    h('h1', { class: 'ui-header-title' }, title), ...extra);
  const body = h('div', { class: 'ui-body' });
  const el = h('section', { 'aria-label': title }, header, body);
  return { el, header, body };
}
